import { useEffect, useMemo, useRef } from "react";

export interface Window {
  start: string;
  end: string;
}

/** Uncontrolled date input that only commits on a real pick or an explicit
 * blur — never on calendar navigation.
 *
 * Browsers fire `change` events on calendar navigation arrows (↑/↓ to step
 * a month) with the new month-shifted value. The day-of-month stays the
 * same; only the month/year changes. We detect that by comparing the new
 * value's day component to the last-committed value's day. If they match,
 * it's navigation — don't commit. If they differ, the user picked a
 * specific date — commit immediately so the dashboard updates.
 *
 * Blur (clicking outside, hitting Escape) always commits, in case the user
 * picked the same day-of-month in a different month (no `change` fires
 * because the picker re-confirms the highlighted day).
 *
 * Input is uncontrolled (defaultValue + ref) so React doesn't reconcile
 * the value attribute and disturb the open picker. */
function DateInput({
  ariaLabel,
  value,
  onCommit,
}: {
  ariaLabel: string;
  value: string;
  onCommit: (v: string) => void;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const lastCommitted = useRef<string>(value);

  useEffect(() => {
    if (ref.current && ref.current.value !== value) {
      ref.current.value = value;
    }
    lastCommitted.current = value;
  }, [value]);

  const commit = (v: string) => {
    if (v && v !== lastCommitted.current) {
      lastCommitted.current = v;
      onCommit(v);
    }
  };

  // YYYY-MM-DD → "DD". Empty string for empty values.
  const dayPart = (s: string) => (s.length === 10 ? s.slice(8, 10) : "");

  return (
    <input
      ref={ref}
      type="date"
      aria-label={ariaLabel}
      defaultValue={value}
      onChange={(e) => {
        const v = e.currentTarget.value;
        // Navigation arrows produce month-shifted values with the same
        // day-of-month. Skip those; let blur handle the rare case where the
        // user actually wants to pick the same day in a new month.
        if (dayPart(v) !== dayPart(lastCommitted.current)) {
          commit(v);
        }
      }}
      onBlur={(e) => commit(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.currentTarget.blur();
        }
      }}
      className="rounded-md border border-slate-300 px-2 py-0.5 text-xs dark:bg-slate-800 dark:border-slate-700"
    />
  );
}

const isoMinusDays = (days: number): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

const monthStart = (offset = 0): string => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offset);
  return d.toISOString().slice(0, 10);
};

export function defaultWindow(): Window {
  return { start: isoMinusDays(90), end: new Date().toISOString().slice(0, 10) };
}

export function WindowPicker({ value, onChange }: { value: Window; onChange: (w: Window) => void }) {
  const presets = useMemo(
    () => [
      { label: "Last 7d", get: (): Window => ({ start: isoMinusDays(7), end: new Date().toISOString().slice(0, 10) }) },
      { label: "Last 30d", get: (): Window => ({ start: isoMinusDays(30), end: new Date().toISOString().slice(0, 10) }) },
      { label: "Last 90d", get: (): Window => ({ start: isoMinusDays(90), end: new Date().toISOString().slice(0, 10) }) },
      { label: "MTD", get: (): Window => ({ start: monthStart(0), end: new Date().toISOString().slice(0, 10) }) },
      { label: "Last month", get: (): Window => ({ start: monthStart(-1), end: monthStart(0) }) },
      // "All time" — far enough back to cover any realistic data; backend
      // bucketing handles arbitrary ranges, chart auto-downsamples to monthly.
      { label: "All time", get: (): Window => ({ start: "2020-01-01", end: new Date().toISOString().slice(0, 10) }) },
    ],
    [],
  );

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1 text-xs">
        {presets.map((p) => (
          <button
            key={p.label}
            onClick={() => onChange(p.get())}
            className="rounded-md px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
          >
            {p.label}
          </button>
        ))}
      </div>
      <span className="text-slate-300 dark:text-slate-600">|</span>
      <label className="flex items-center gap-1 text-xs text-slate-500">
        <span>From</span>
        <DateInput
          ariaLabel="From date"
          value={value.start}
          onCommit={(v) => onChange({ ...value, start: v })}
        />
      </label>
      <label className="flex items-center gap-1 text-xs text-slate-500">
        <span>To</span>
        <DateInput
          ariaLabel="To date"
          value={value.end}
          onCommit={(v) => onChange({ ...value, end: v })}
        />
      </label>
    </div>
  );
}
